// Tiny structured-query helpers shared by classic + advanced search modes.
// The backend syntax is e621-compatible; these utilities just compose and
// decompose the same string format.

export type FileType =
  | ""
  | "image"   // sentinel — backend sees it as no type filter + include_images flag
  | "webm"
  | "gif"
  | "apng"
  | "jpg"
  | "png"
  | "webp";

export type OrderKey =
  | ""
  | "id_desc"
  | "id_asc"
  | "score"
  | "score_asc"
  | "favcount"
  | "random";

export interface StructuredQuery {
  must: string[];     // tags that must be present (no prefix)
  exclude: string[];  // tags that must NOT be present (compiled with "-")
  type: FileType;     // ""=all animations | webm | gif | apng
  order: OrderKey;
}

const META_PREFIXES = ["type:", "order:"];

function isMetaToken(token: string): boolean {
  return META_PREFIXES.some((p) => token.toLowerCase().startsWith(p));
}

function splitTagList(raw: string): string[] {
  // Accept commas or whitespace as separators.
  return raw
    .split(/[\s,]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

/** Compose a structured query into the backend string format. The
 *  ``image`` sentinel becomes no ``type:`` clause — image inclusion is
 *  controlled via the ``include_images`` request param, not query syntax. */
export function composeQuery(q: StructuredQuery): string {
  const parts: string[] = [];
  for (const t of q.must) parts.push(t);
  for (const t of q.exclude) parts.push(t.startsWith("-") ? t : `-${t}`);
  if (q.type && q.type !== "image") parts.push(`type:${q.type}`);
  if (q.order) parts.push(`order:${q.order}`);
  return parts.join(" ").trim();
}

/** Best-effort decomposition of a raw query string into structured parts.
 *  Unknown tokens fall into ``must``. */
export function parseQuery(raw: string): StructuredQuery {
  const out: StructuredQuery = { must: [], exclude: [], type: "", order: "" };
  for (const token of raw.split(/\s+/).filter(Boolean)) {
    const low = token.toLowerCase();
    if (low.startsWith("type:")) {
      const val = low.slice(5);
      if (
        val === "webm" ||
        val === "gif" ||
        val === "apng" ||
        val === "jpg" ||
        val === "png" ||
        val === "webp"
      ) {
        out.type = val;
      }
      continue;
    }
    if (low.startsWith("order:")) {
      const val = low.slice(6) as OrderKey;
      out.order = val;
      continue;
    }
    if (token.startsWith("-")) {
      out.exclude.push(token.slice(1));
      continue;
    }
    if (!isMetaToken(token)) {
      out.must.push(token);
    }
  }
  return out;
}

/** Return ``raw`` with ``type:gif`` ensured present (no duplication).
 *  Used by classic mode's "GIFs only" toggle. */
export function withGifsOnly(raw: string): string {
  const tokens = raw.split(/\s+/).filter(Boolean);
  const filtered = tokens.filter((t) => !t.toLowerCase().startsWith("type:"));
  filtered.push("type:gif");
  return filtered.join(" ");
}

/** Strip any ``type:`` token from a raw query — used when toggling GIFs Only off. */
export function withoutTypeFilter(raw: string): string {
  return raw
    .split(/\s+/)
    .filter(Boolean)
    .filter((t) => !t.toLowerCase().startsWith("type:"))
    .join(" ");
}

export { splitTagList };
